import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";

import { brandThemeCssVars, DEFAULT_BRAND_THEME } from "../themeTokens";

const SPEC_SEPARATOR = "|";
const ID_LIKE_RE =
  /^([0-9]+|[0-9a-f]{8,}(-[0-9a-f]{4,}){2,}|[A-Za-z0-9_-]{18,})$/i;

function readableLabel(value) {
  if (!value) return "";

  if (value && typeof value === "object") {
    const nested = value.allergen || value.alergen || value.ingredient || value.value;
    if (nested && typeof nested === "object") {
      return readableLabel(nested);
    }

    return String(value.name || value.shortName || value.label || "").trim();
  }

  const text = String(value).trim();
  return ID_LIKE_RE.test(text) ? "" : text;
}

function firstValue(product, keys) {
  for (const key of keys) {
    const value = product?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function asList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((x) => {
        return readableLabel(x);
      })
      .filter(Boolean);
  }

  return String(value)
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter((x) => !ID_LIKE_RE.test(x))
    .filter(Boolean);
}

export function parseProductSpecSections(spec) {
  if (!spec || typeof spec !== "string") return [];

  return spec
    .split(SPEC_SEPARATOR)
    .map((block) => {
      const lines = block
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      const title = lines.shift() || "";
      const body = lines.join("\n");

      return { title, body };
    })
    .filter((section) => section.title || section.body);
}

function getNutritionRows(product) {
  const rows = [
    {
      key: "nutrition.energy",
      label: "Energie",
      value: firstValue(product, ["kcal", "calories", "energyKcal"]),
      suffix: "kcal",
      icon: <LocalFireDepartmentOutlinedIcon fontSize="small" />,
    },
    {
      key: "nutrition.protein",
      label: "Proteine",
      value: firstValue(product, ["protein", "proteins", "proteine"]),
      suffix: "g",
    },
    {
      key: "nutrition.carbs",
      label: "Carbohidrați",
      value: firstValue(product, ["carbs", "carbohydrates", "carbohidrati"]),
      suffix: "g",
    },
    {
      key: "nutrition.sugars",
      label: "Zaharuri",
      value: firstValue(product, ["sugars", "sugar", "zaharuri"]),
      suffix: "g",
    },
    {
      key: "nutrition.fat",
      label: "Grăsimi",
      value: firstValue(product, ["fat", "fats", "grasimi"]),
      suffix: "g",
    },
    {
      key: "nutrition.salt",
      label: "Sare",
      value: firstValue(product, ["salt", "sare"]),
      suffix: "g",
    },
    {
      key: "nutrition.weight",
      label: "Gramaj",
      value: firstValue(product, ["weight", "grammage", "grams", "gramaj"]),
      suffix: "g",
    },
  ];

  return rows
    .filter((row) => row.value !== "")
    .map((row) => ({
      ...row,
      value: `${row.value}${row.suffix && !String(row.value).includes(row.suffix) ? ` ${row.suffix}` : ""}`,
    }));
}

function InfoSection({ title, children }) {
  return (
    <Box
      component="section"
      sx={{
        borderRadius: 2.5,
        border: "1px solid rgba(124,45,18,0.10)",
        bgcolor:
          "color-mix(in srgb, var(--brand-surface, #fff) 92%, transparent)",
        p: { xs: 1.5, sm: 1.75 },
        boxShadow: "0 8px 24px rgba(33,20,12,0.04)",
      }}
    >
      <Typography sx={{ fontWeight: 950, color: "var(--brand-text, #431407)", mb: 0.75 }}>
        {title}
      </Typography>

      {children}
    </Box>
  );
}

export default function ProductInfoModal({
  open,
  onClose,
  product,
  categoryName = "",
  brandTheme = DEFAULT_BRAND_THEME,
  allergens = [],
  t = (key, fallback) => fallback || key,
}) {
  const c = brandTheme;
  const specSections = useMemo(() => parseProductSpecSections(product?.spec), [product]);
  const allergenIcons = useMemo(
    () => (Array.isArray(allergens) ? allergens : []).filter((allergen) => allergen?.iconUrl),
    [allergens]
  );
  const ingredients = useMemo(
    () => asList(firstValue(product, ["ingredients", "ingredientList", "composition", "compozitie"])),
    [product]
  );
  const nutritionRows = useMemo(() => getNutritionRows(product), [product]);
  const hasDetails =
    Boolean(product?.description) ||
    allergenIcons.length > 0 ||
    ingredients.length > 0 ||
    nutritionRows.length > 0 ||
    specSections.length > 0;

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="product-info-title"
      PaperProps={{
        sx: {
          ...brandThemeCssVars(c),
          m: { xs: 1, sm: 4 },
          width: { xs: "calc(100% - 16px)", sm: "100%" },
          maxHeight: { xs: "calc(100dvh - 16px)", sm: "calc(100% - 64px)" },
          borderRadius: { xs: 3, sm: 3.5 },
          overflow: "hidden",
          bgcolor: c.background,
          boxShadow: "0 28px 80px rgba(33,20,12,0.22)",
          "@media (prefers-reduced-motion: reduce)": {
            "&, & *": {
              animationDuration: "0.01ms !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      }}
    >
      <DialogTitle
        component="header"
        sx={{
          p: { xs: 1.5, sm: 2 },
          bgcolor: c.surface,
          borderBottom: "1px solid rgba(124,45,18,0.10)",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.25,
              bgcolor: c.primary,
              color: c.onPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <InfoOutlinedIcon />
          </Box>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              id="product-info-title"
              sx={{
                fontWeight: 950,
                color: c.text,
                lineHeight: 1.2,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {product.name}
            </Typography>

            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.75 }}>
              {categoryName ? (
                <Chip
                  size="small"
                  icon={<RestaurantMenuRoundedIcon />}
                  label={categoryName}
                  sx={{
                    fontWeight: 850,
                    bgcolor: "rgba(255,250,243,0.92)",
                    color: c.text,
                    border: "1px solid rgba(124,45,18,0.10)",
                  }}
                />
              ) : null}

              {product.isNew ? (
                <Chip
                  size="small"
                  label={t("productInfo.new")}
                  sx={{ fontWeight: 850, bgcolor: c.primary, color: c.onPrimary }}
                />
              ) : null}
            </Stack>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label={t("productInfo.close")}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.25,
              flexShrink: 0,
              color: c.text,
              bgcolor:
                "color-mix(in srgb, var(--brand-bg, #fffaf3) 72%, transparent)",
              border: "1px solid rgba(124,45,18,0.10)",
              "&:focus-visible": {
                outline: `3px solid ${c.cta}`,
                outlineOffset: 2,
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2 },
          overscrollBehavior: "contain",
          "&:first-of-type": {
            pt: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Stack spacing={1.25}>
          {product.description ? (
            <InfoSection title={t("productInfo.description")}>
              <Typography color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.65 }}>
                {product.description}
              </Typography>
            </InfoSection>
          ) : null}

          {ingredients.length ? (
            <InfoSection title={t("productInfo.ingredients")}>
              <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
                {ingredients.join(", ")}
              </Typography>
            </InfoSection>
          ) : null}

          {allergenIcons.length ? (
            <InfoSection title={t("productInfo.allergens")}>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
                {allergenIcons.map((allergen) => (
                  <Box
                    key={allergen.iref}
                    title={allergen.name || t("productInfo.allergens")}
                    sx={{
                      position: "relative",
                      width: 44,
                      height: 44,
                      borderRadius: 2.25,
                      bgcolor: "rgba(255,248,225,0.92)",
                      border: "1px solid rgba(124,45,18,0.10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      aria-hidden="true"
                      sx={{
                        color: c.muted,
                        fontWeight: 900,
                        textTransform: "uppercase",
                      }}
                    >
                      {(allergen.name || "?").charAt(0)}
                    </Typography>
                    <Box
                      component="img"
                      src={allergen.iconUrl}
                      alt={allergen.name || t("productInfo.allergens")}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                      sx={{
                        position: "absolute",
                        width: 28,
                        height: 28,
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </InfoSection>
          ) : null}

          {nutritionRows.length ? (
            <InfoSection title={t("productInfo.nutrition")}>
              <Stack divider={<Divider flexItem />} spacing={0.75}>
                {nutritionRows.map((row) => (
                  <Stack
                    key={row.key || row.label}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                      {row.icon ? (
                        <Box sx={{ display: "flex", color: c.accent }}>{row.icon}</Box>
                      ) : null}

                      <Typography color="text.secondary" sx={{ fontWeight: 800 }}>
                        {t(row.key, row.label)}
                      </Typography>
                    </Stack>

                    <Typography sx={{ fontWeight: 950, color: c.text, whiteSpace: "nowrap" }}>
                      {row.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </InfoSection>
          ) : null}

          {specSections.map((section) => (
            <InfoSection key={`${section.title}-${section.body}`} title={section.title || t("productInfo.details")}>
              <Typography color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.65 }}>
                {section.body}
              </Typography>
            </InfoSection>
          ))}

          {!hasDetails ? (
            <InfoSection title={t("productInfo.emptyTitle")}>
              <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
                {t("productInfo.emptyText")}
              </Typography>
            </InfoSection>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
